import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { defaultExpenseCategories, defaultIncomeCategories, getIcon, acquisitionSources, catColor, catTint } from '../lib/constants';
import { Wallet, ArrowRight, ArrowLeft, CheckCircle, Target, Tag, Sparkles, CalendarClock, Bell, Compass, Mic, MessageCircle } from 'lucide-react';
import { isPushSupported, isIOS, isStandalone, enablePush, localTimeToUtc } from '../lib/push';
import { sendTestPush } from '../lib/api';
import { friendlyError } from '../lib/errorReporting';

const steps = [
  { title: 'Bienvenido', icon: Sparkles },
  { title: 'Moneda', icon: Wallet },
  { title: 'Objetivo', icon: Target },
  { title: 'Presupuesto', icon: CalendarClock },
  { title: 'Categorías', icon: Tag },
  { title: 'Notificaciones', icon: Bell },
  { title: 'Origen', icon: Compass },
  { title: 'Listo', icon: CheckCircle }
];

export default function Onboarding({ onComplete }) {
  const [step, setStep] = useState(0);
  // Drives which slide-in animation direction plays next (see App.css) — so
  // "Atras" visibly reverses the path "Siguiente" took instead of replaying
  // the same forward motion both ways.
  const [direction, setDirection] = useState('forward');
  const goNext = () => { setDirection('forward'); setStep(s => s + 1); };
  const goBack = () => { setDirection('backward'); setStep(s => s - 1); };
  const [formData, setFormData] = useState({
    name: '',
    currency: 'USD',
    monthly_goal: '',
    selected_categories: [],
    financial_goal: 'ahorrar',
    budget_period: 'monthly',
    notifications_enabled: false,
    reminder_time: '',
    reminder_time_utc: '',
    tz_offset: 0,
    acquisition_source: '',
    acquisition_source_other: '',
  });
  const [notifBusy, setNotifBusy] = useState(false);
  const [notifError, setNotifError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const allCategories = [...defaultExpenseCategories, ...defaultIncomeCategories];
  const progress = ((step + 1) / steps.length) * 100;

  // Combines "activate" and "next" into one action — a real person in the
  // wild clicked the bottom Siguiente button expecting it to also turn
  // notifications on, ignored the separate activate button entirely, and
  // ended up with notifications off with no idea why. Success advances the
  // step immediately so there's nothing left to separately click; failure
  // stays put so the error is visible and "Continuar sin notificaciones"
  // remains the explicit way to move on.
  const handleActivateAndContinue = async () => {
    setNotifBusy(true);
    setNotifError('');
    try {
      await enablePush();
      const reminderTime = '20:00';
      setFormData(prev => ({
        ...prev,
        notifications_enabled: true,
        reminder_time: reminderTime,
        reminder_time_utc: localTimeToUtc(reminderTime),
        tz_offset: new Date().getTimezoneOffset(),
      }));
      try { await sendTestPush(); } catch (_) { /* best effort */ }
      goNext();
    } catch (e) {
      setNotifError(friendlyError(e, 'No se pudieron activar las notificaciones'));
    } finally {
      setNotifBusy(false);
    }
  };

  const toggleCategory = (name) => {
    setFormData(prev => ({
      ...prev,
      selected_categories: prev.selected_categories.includes(name)
        ? prev.selected_categories.filter(c => c !== name)
        : [...prev.selected_categories, name]
    }));
  };

  const handleSubmit = async () => {
    // Previously fire-and-forget — if onComplete's API call failed (network
    // blip, timeout, server error), nothing here ever knew: no spinner, no
    // error, no way to retry, and the click just silently did nothing. Found
    // via a handful of real accounts stuck with a login but no profile.
    if (submitting) return;
    setSubmitting(true);
    try {
      await onComplete({
        ...formData,
        monthly_goal: parseFloat(formData.monthly_goal) || 0
      });
    } catch (e) {
      // onComplete already surfaces a toast — just stop spinning so "Comenzar" is clickable again.
    } finally {
      setSubmitting(false);
    }
  };

  const canNext = () => {
    if (step === 0) return formData.name.trim().length > 0;
    if (step === 4) return formData.selected_categories.length > 0;
    if (step === 6) return formData.acquisition_source && (formData.acquisition_source !== 'other' || formData.acquisition_source_other.trim().length > 0);
    return true;
  };

  // Whether the notifications step renders its own combined activate+continue
  // button (see handleActivateAndContinue) instead of the generic bottom
  // Siguiente — false for the unsupported-browser / iOS-not-installed
  // fallback screens, which have no action to combine with Next.
  const notifStepHasOwnCTA = isPushSupported() && !(isIOS() && !isStandalone());
  const slideClass = direction === 'backward' ? 'animate-slide-in-left' : 'animate-slide-in';

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative"
      style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))', paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
      data-testid="onboarding-page"
    >
      {/* Background glow */}
      {/* No CSS blur() filter here on purpose: a 120px blur radius is one of
          the most GPU-expensive compositing operations there is, and on
          low/mid-end Android it's a known cause of the renderer process
          crashing to a black screen (reported: onboarding went black,
          especially on touch, on low-end Android — fixed for one user by
          installing as a PWA, which points at a GPU/renderer crash rather
          than app logic). The radial-gradient already fades to transparent
          on its own, so the blur wasn't adding much visually — this keeps
          the same soft glow at zero compositing cost. */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full opacity-20 pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(139, 92, 246, 0.45) 0%, transparent 70%)' }} />

      <div className="w-full max-w-lg relative">
        {/* Brand mark, same gradient as the app's main block */}
        <div className="flex items-center justify-center gap-2.5 mb-6">
          <div className="brand-hero w-10 h-10 rounded-xl flex items-center justify-center text-on-brand text-xl font-extrabold">$</div>
          <span className="text-xl font-bold tracking-tight text-white">MiPisto</span>
        </div>

        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            {steps.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className={`flex items-center gap-1.5 text-xs font-medium transition-colors duration-300 ${i <= step ? 'text-violet-400' : 'text-slate-400'}`}>
                  <Icon className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">{s.title}</span>
                </div>
              );
            })}
          </div>
          <div className="h-1.5 w-full rounded-full bg-slate-800 overflow-hidden" data-testid="onboarding-progress" role="progressbar" aria-valuenow={Math.round(progress)} aria-valuemin={0} aria-valuemax={100}>
            <div className="h-full rounded-full brand-gradient-bar transition-[width] duration-500" style={{ width: `${progress}%` }} />
          </div>
          {/* Icon-only row above has no room for labels on small screens
              (hidden sm:inline) — this line is the mobile-only "where am I"
              readout so wayfinding doesn't disappear exactly on the device
              class this app is built mobile-first for. */}
          <p className="sm:hidden text-xs text-slate-500 mt-2 text-center">
            Paso {step + 1} de {steps.length} · {steps[step].title}
          </p>
        </div>

        {/* Card */}
        <div className="glass-card rounded-2xl p-8 shadow-lg shadow-black/20 noise-overlay relative overflow-hidden">
          {/* Step 0: Welcome */}
          {step === 0 && (
            <div className={slideClass} data-testid="onboarding-step-0">
              <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">¡Hola!</h1>
              <p className="text-slate-400 mb-8 text-base">Comencemos a organizar tus finanzas. ¿Cómo te llamas?</p>
              <div className="space-y-2">
                <Label htmlFor="name" className="text-slate-300">Tu nombre</Label>
                <Input
                  id="name"
                  data-testid="onboarding-name-input"
                  placeholder="Ej: Carlos"
                  value={formData.name}
                  onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="bg-slate-950/50 border-slate-700 focus:border-violet-500/50 h-12 rounded-xl text-white placeholder:text-slate-400"
                />
              </div>
            </div>
          )}

          {/* Step 1: Currency */}
          {step === 1 && (
            <div className={slideClass} data-testid="onboarding-step-1">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">Moneda</h2>
              <p className="text-slate-400 mb-8">Selecciona tu moneda principal</p>
              <div className="space-y-2">
                <Label className="text-slate-300">Moneda</Label>
                <Select value={formData.currency} onValueChange={v => setFormData(prev => ({ ...prev, currency: v }))}>
                  <SelectTrigger className="bg-slate-950/50 border-slate-700 h-12 rounded-xl text-white" data-testid="onboarding-currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    <SelectItem value="USD">USD - Dólar estadounidense</SelectItem>
                    <SelectItem value="EUR">EUR - Euro</SelectItem>
                    <SelectItem value="MXN">MXN - Peso mexicano</SelectItem>
                    <SelectItem value="COP">COP - Peso colombiano</SelectItem>
                    <SelectItem value="ARS">ARS - Peso argentino</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Step 2: Goal */}
          {step === 2 && (
            <div className={slideClass} data-testid="onboarding-step-2">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">Tu objetivo</h2>
              <p className="text-slate-400 mb-8">¿Qué quieres lograr con tus finanzas?</p>
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label className="text-slate-300">Objetivo financiero</Label>
                  <Select value={formData.financial_goal} onValueChange={v => setFormData(prev => ({ ...prev, financial_goal: v }))}>
                    <SelectTrigger className="bg-slate-950/50 border-slate-700 h-12 rounded-xl text-white" data-testid="onboarding-goal-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700">
                      <SelectItem value="ahorrar">Ahorrar más dinero</SelectItem>
                      <SelectItem value="controlar">Controlar mis gastos</SelectItem>
                      <SelectItem value="deudas">Pagar deudas</SelectItem>
                      <SelectItem value="invertir">Empezar a invertir</SelectItem>
                      <SelectItem value="presupuesto">Crear un presupuesto</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="goal" className="text-slate-300">Meta mensual de ahorro ($)</Label>
                  <Input
                    id="goal"
                    data-testid="onboarding-monthly-goal-input"
                    type="number"
                    placeholder="Ej: 500"
                    value={formData.monthly_goal}
                    onChange={e => setFormData(prev => ({ ...prev, monthly_goal: e.target.value }))}
                    className="bg-slate-950/50 border-slate-700 focus:border-violet-500/50 h-12 rounded-xl text-white placeholder:text-slate-400"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Budget Period */}
          {step === 3 && (
            <div className={slideClass} data-testid="onboarding-step-3">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">Periodo de presupuesto</h2>
              <p className="text-slate-400 mb-8">¿Cada cuánto quieres que se reinicien tus presupuestos?</p>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  data-testid="budget-period-monthly"
                  onClick={() => setFormData(prev => ({ ...prev, budget_period: 'monthly' }))}
                  className={`flex flex-col items-center gap-3 p-6 rounded-2xl border transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] ${
                    formData.budget_period === 'monthly'
                      ? 'border-violet-500/50 bg-violet-500/10 text-white'
                      : 'border-slate-700/50 bg-slate-900/50 text-slate-400 hover:border-slate-600'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    formData.budget_period === 'monthly' ? 'bg-violet-500/20' : 'bg-slate-800'
                  }`}>
                    <CalendarClock className={`w-6 h-6 ${formData.budget_period === 'monthly' ? 'text-violet-400' : ''}`} />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-semibold">Mensual</p>
                    <p className="text-xs text-slate-500 mt-1">Se reinicia el 1.º de cada mes</p>
                  </div>
                </button>
                <button
                  type="button"
                  data-testid="budget-period-weekly"
                  onClick={() => setFormData(prev => ({ ...prev, budget_period: 'weekly' }))}
                  className={`flex flex-col items-center gap-3 p-6 rounded-2xl border transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] ${
                    formData.budget_period === 'weekly'
                      ? 'border-violet-500/50 bg-violet-500/10 text-white'
                      : 'border-slate-700/50 bg-slate-900/50 text-slate-400 hover:border-slate-600'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    formData.budget_period === 'weekly' ? 'bg-violet-500/20' : 'bg-slate-800'
                  }`}>
                    <CalendarClock className={`w-6 h-6 ${formData.budget_period === 'weekly' ? 'text-violet-400' : ''}`} />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-semibold">Semanal</p>
                    <p className="text-xs text-slate-500 mt-1">Se reinicia cada lunes</p>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Categories */}
          {step === 4 && (
            <div className={slideClass} data-testid="onboarding-step-4">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">Categorías</h2>
              <p className="text-slate-400 mb-6">Selecciona las categorías que usas</p>
              <div className="grid grid-cols-3 gap-3">
                {allCategories.map(cat => {
                  const Icon = getIcon(cat.icon);
                  const selected = formData.selected_categories.includes(cat.name);
                  return (
                    <button
                      key={cat.name}
                      data-testid={`onboarding-category-${cat.name}`}
                      onClick={() => toggleCategory(cat.name)}
                      className={`flex flex-col items-center gap-2 p-4 rounded-xl border transition-all duration-200 hover:scale-[1.03] active:scale-[0.97] ${
                        selected
                          ? 'border-violet-500/50 bg-violet-500/10 text-white'
                          : 'border-slate-700/50 bg-slate-900/50 text-slate-400 hover:border-slate-600'
                      }`}
                    >
                      <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: selected ? catTint(cat.color) : 'transparent' }}>
                        <Icon className="w-5 h-5" style={{ color: selected ? catColor(cat.color) : undefined }} />
                      </div>
                      <span className="text-xs font-medium">{cat.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 5: Notifications */}
          {step === 5 && (
            <div className={slideClass} data-testid="onboarding-step-5">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">Notificaciones</h2>
              <p className="text-slate-400 mb-8">
                Te avisamos si te acercas a un presupuesto, tus rachas de registro, y un
                recordatorio diario para no perder el hilo. Nada de spam.
              </p>

              {!isPushSupported() ? (
                <div className="rounded-xl bg-slate-900/50 border border-white/10 p-4 text-sm text-slate-400">
                  Tu navegador no soporta notificaciones push. Puedes activarlas luego desde Configuración.
                </div>
              ) : isIOS() && !isStandalone() ? (
                <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 p-4 text-sm text-amber-300">
                  En iPhone/iPad, primero añade MiPisto a tu pantalla de inicio
                  (Compartir → "Añadir a inicio"). Luego abre la app desde el ícono
                  para activar las notificaciones.
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    data-testid="onboarding-enable-notifications-btn"
                    onClick={handleActivateAndContinue}
                    disabled={notifBusy}
                    className="w-full flex items-center justify-center gap-2 brand-cta text-on-brand h-12 rounded-xl font-semibold transition-all duration-200 active:scale-[0.97] disabled:opacity-50"
                  >
                    {notifBusy ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <Bell className="w-4 h-4" />
                    )}
                    Activar y continuar
                  </button>
                  {notifError && (
                    <p className="text-xs text-rose-400 mt-3 text-center">{notifError}</p>
                  )}
                  <button
                    type="button"
                    data-testid="onboarding-skip-notifications-btn"
                    onClick={goNext}
                    disabled={notifBusy}
                    className="w-full text-center text-sm text-slate-500 hover:text-slate-300 mt-4 transition-colors disabled:opacity-50 active:opacity-60"
                  >
                    Continuar sin notificaciones
                  </button>
                </>
              )}
              <p className="text-xs text-slate-500 mt-3 text-center">
                Puedes cambiar esto cuando quieras desde Configuración.
              </p>
            </div>
          )}

          {/* Step 6: Acquisition source */}
          {step === 6 && (
            <div className={slideClass} data-testid="onboarding-step-6">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">Una pregunta rápida</h2>
              <p className="text-slate-400 mb-6">¿Cómo encontraste MiPisto?</p>
              <div className="grid grid-cols-2 gap-2.5">
                {acquisitionSources.map(opt => {
                  const Icon = opt.icon;
                  const selected = formData.acquisition_source === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      data-testid={`onboarding-acquisition-source-${opt.value}`}
                      onClick={() => setFormData(prev => ({ ...prev, acquisition_source: opt.value }))}
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-medium text-left transition-all duration-200 ${
                        selected
                          ? 'border-violet-500/50 bg-violet-500/10 text-white'
                          : 'border-slate-700/50 bg-slate-900/50 text-slate-400 hover:border-slate-600'
                      }`}
                    >
                      <Icon className="w-4 h-4 shrink-0" />
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              {formData.acquisition_source === 'other' && (
                <Input
                  data-testid="onboarding-acquisition-source-other-input"
                  placeholder="Cuéntanos dónde..."
                  value={formData.acquisition_source_other}
                  onChange={e => setFormData(prev => ({ ...prev, acquisition_source_other: e.target.value }))}
                  className="w-full mt-3 bg-slate-950/50 border-slate-700 focus:border-violet-500/50 h-11 rounded-xl text-white placeholder:text-slate-400"
                />
              )}
            </div>
          )}

          {/* Step 7: Confirmation */}
          {step === 7 && (
            <div className={`${slideClass} text-center`} data-testid="onboarding-step-7">
              <div className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-emerald-400" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">¡Todo listo, {formData.name}!</h2>
              <p className="text-slate-400 mb-6">Tu espacio de finanzas está configurado. Esto es lo que vas a encontrar:</p>
              <ul className="brand-hero rounded-2xl p-4 mb-4 space-y-3 text-left text-on-brand" data-testid="onboarding-whats-inside">
                <li className="flex items-start gap-3">
                  <Wallet className="w-5 h-5 shrink-0 mt-0.5 text-on-brand/80" />
                  <span className="text-sm leading-snug"><b>Lo que te queda</b> y cuánto puedes gastar al día, apenas abres la app.</span>
                </li>
                <li className="flex items-start gap-3">
                  <Mic className="w-5 h-5 shrink-0 mt-0.5 text-on-brand/80" />
                  <span className="text-sm leading-snug"><b>Anota en segundos</b>: a mano, por voz o solo desde tu Gmail.</span>
                </li>
                <li className="flex items-start gap-3">
                  <MessageCircle className="w-5 h-5 shrink-0 mt-0.5 text-on-brand/80" />
                  <span className="text-sm leading-snug"><b>Tu Coach</b> te responde con tus números cuando tengas dudas.</span>
                </li>
              </ul>
              <div className="space-y-3 text-left bg-slate-900/50 rounded-xl p-4 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Moneda</span>
                  <span className="text-white font-medium">{formData.currency}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Meta mensual</span>
                  <span className="text-white font-medium">${formData.monthly_goal || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Presupuesto</span>
                  <span className="text-white font-medium">{formData.budget_period === 'weekly' ? 'Semanal' : 'Mensual'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Categorías</span>
                  <span className="text-white font-medium">{formData.selected_categories.length} seleccionadas</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Notificaciones</span>
                  <span className="text-white font-medium">{formData.notifications_enabled ? 'Activadas' : 'Desactivadas'}</span>
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-8">
            {step > 0 ? (
              <Button
                data-testid="onboarding-back-btn"
                variant="ghost"
                onClick={goBack}
                className="text-slate-400 hover:text-white hover:bg-white/5 rounded-full px-6 transition-all duration-150 active:scale-[0.97]"
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Atrás
              </Button>
            ) : <div />}

            {step === 5 && notifStepHasOwnCTA ? null : step < 7 ? (
              <Button
                data-testid="onboarding-next-btn"
                onClick={goNext}
                disabled={!canNext()}
                className="brand-cta text-on-brand rounded-full px-8 transition-all duration-150 active:scale-[0.97] disabled:opacity-40 disabled:shadow-none disabled:active:scale-100"
              >
                Siguiente <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                data-testid="onboarding-finish-btn"
                onClick={handleSubmit}
                disabled={submitting}
                className="brand-cta text-on-brand rounded-full px-8 transition-all duration-150 active:scale-[0.97] disabled:opacity-60 disabled:active:scale-100"
              >
                {submitting ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>Comenzar <Sparkles className="w-4 h-4 ml-2" /></>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
